import { IconButton } from '@components/core/icon-button.component';
import { MainLayout } from '@components/layouts/main.layout';
import { HelmetWrapper } from '@components/shared/helmet.component';
import { PageviewTracker } from '@components/shared/pageview-tracker.component';
import { Tile } from '@components/shared/tile.component';
import { Button } from '@components/ui/button';
import { useRequireAdmin } from '@hooks/use-require-admin.hook';
import { useAdminDomainsQuery } from '@queries/admin-domains.query';
import { PlusIcon, RefreshCcwIcon } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { AdminDomainCreateModal } from './components/admin-domain-create-modal.component';
import { AdminDomainModal } from './components/admin-domain-modal.component';
import { AdminDomainsPagination } from './components/admin-domains-pagination.component';
import { AdminDomainsTable } from './components/admin-domains-table.component';
import { useAdminDomainsQueryParams } from './hooks/use-admin-domains-query-params.hook';

export const AdminDomainsPage: React.FC = () => {
  const { isAdmin, isPending: isAdminPending } = useRequireAdmin();
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const {
    DEFAULT_SORTING,
    handleLimitChange,
    page,
    queryParams,
    search,
    selectedDomainId,
    setDomainType,
    setPage,
    setSearch,
    setSelectedDomainId,
    setSorting,
    sortingState,
    type,
  } = useAdminDomainsQueryParams();

  const { data, isPending, isFetching, refetch: refetchDomains } = useAdminDomainsQuery(queryParams);

  const domains = data?.domains ?? [];
  const pagination = data?.pagination;

  useEffect(() => {
    if (!pagination) return;

    const current = page;
    const total = pagination.totalPages ?? 0;

    if (total < 1) {
      if (current !== 1) void setPage(1);
      return;
    }

    if (current > total) {
      void setPage(Math.max(1, total));
    }
  }, [page, pagination, setPage]);

  if (isAdminPending || !isAdmin) {
    return null;
  }

  return (
    <PageviewTracker>
      <HelmetWrapper title="Admin Domains" />
      <MainLayout
        asideClassName="hidden"
        contentClassName="w-full md:max-w-full"
      >
        <div className="flex flex-col w-full gap-6">
          <Tile
            header={{
              title: 'Domains',
              actions: (
                <div className="flex items-center gap-2">
                  <IconButton
                    ariaLabel="Refresh domains"
                    disabled={isFetching}
                    icon={<RefreshCcwIcon />}
                    variant="secondary"
                    onClick={() => {
                      refetchDomains();
                    }}
                  />
                  <Button onClick={() => setIsCreateOpen(true)}>
                    <PlusIcon className="size-3 mr-1" />
                    New Domain
                  </Button>
                </div>
              ),
            }}
          >
            <div className="flex flex-col gap-4">
              <AdminDomainsTable
                domains={domains}
                domainType={type}
                isLoading={isPending}
                onRowClick={(domainId) => void setSelectedDomainId(domainId)}
                onSearchChange={(value) => {
                  void setSearch(value);
                  void setPage(1);
                }}
                onSortingChange={(state) => {
                  if (!state || state.length === 0) {
                    void setSorting(DEFAULT_SORTING.map((item) => ({ ...item })));
                    return;
                  }
                  void setSorting(state);
                }}
                onTypeChange={(value) => {
                  void setDomainType(value);
                  void setPage(1);
                }}
                search={search}
                sorting={sortingState}
              />

              {pagination && (
                <AdminDomainsPagination
                  isFetching={isFetching}
                  pagination={pagination}
                  onLimitChange={handleLimitChange}
                  onNextPage={() => void setPage(page + 1)}
                  onPreviousPage={() => void setPage(page - 1)}
                />
              )}
            </div>
          </Tile>
        </div>
      </MainLayout>

      <AdminDomainCreateModal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
      />

      {selectedDomainId && (
        <AdminDomainModal
          domainId={selectedDomainId}
          isOpen
          onClose={() => void setSelectedDomainId(null)}
        />
      )}
    </PageviewTracker>
  );
};
